import { Box, ButtonBase, Stack, alpha, useTheme } from '@mui/material';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import {
  DropZoneButton,
  FullScreenDropZone,
} from '@modules/_shared/components/DropZone';
import { ALLOWED_MIMETYPES_FOR_PUBLICATION } from '@modules/_shared/components/PropertyFiles';
import { useLocale } from '@shared/hooks/locale';

export type MediaKind = 'images' | 'videos' | 'documents';

const MEDIA_ACCEPT_LIST = [
  'image/*',
  ...ALLOWED_MIMETYPES_FOR_PUBLICATION,
].join(', ');

const MediaUploadDropArea = ({
  onSelect,
  isUploading,
}: {
  onSelect: (files: File[]) => void;
  isUploading: boolean;
}) => {
  const { t } = useLocale();

  return (
    <>
      <FullScreenDropZone
        onDrop={files => {
          if (files.length > 0) {
            onSelect(files);
          }
        }}
        accept={MEDIA_ACCEPT_LIST}
        acceptLabel={t('Images, videos and documents')}
        disabled={isUploading}
      />
      <DropZoneButton
        onDrop={onSelect}
        accept={MEDIA_ACCEPT_LIST}
        multiple
        isLoading={isUploading}
      />
    </>
  );
};

export const MediaHeader = ({
  counts,
  activeTab,
  onUpload,
  isUploading,
  readOnly = false,
}: {
  counts: Record<MediaKind, number>;
  activeTab: MediaKind;
  onUpload: (files: File[]) => void;
  isUploading: boolean;
  readOnly?: boolean;
}) => {
  const { t } = useLocale();
  const theme = useTheme();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const mediaBase = pathname.slice(
    0,
    pathname.indexOf('/media') + '/media'.length,
  );

  const subTabs = [
    { kind: 'images', label: t('Images') },
    { kind: 'videos', label: t('Videos') },
    { kind: 'documents', label: t('Documents') },
  ] as const;

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: (theme.vars ?? theme).palette.background.paper,
        borderBottom: `1px solid ${(theme.vars ?? theme).palette.divider}`,
      }}
    >
      {!readOnly && (
        <Box sx={{ pt: 0.5 }}>
          <MediaUploadDropArea onSelect={onUpload} isUploading={isUploading} />
        </Box>
      )}
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ flexWrap: 'wrap', px: 1.5, py: 1 }}
      >
        {subTabs.map(({ kind, label }) => {
          const active = kind === activeTab;
          const color = active ? 'primary.main' : 'text.secondary';
          const activeBg = alpha(theme.palette.primary.main, 0.16);
          const inactiveBg = (theme.vars ?? theme).palette.action.hover;
          return (
            <ButtonBase
              key={kind}
              component={Link}
              to={{
                pathname: `${mediaBase}/${kind}`,
                search: searchParams.toString(),
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                height: 32,
                px: 1.5,
                borderRadius: 1,
                fontSize: '0.875rem',
                fontWeight: 600,
                color,
                backgroundColor: active
                  ? alpha(theme.palette.primary.main, 0.1)
                  : 'transparent',
                '&:hover': {
                  backgroundColor: active ? activeBg : inactiveBg,
                },
              }}
            >
              {label}
              <Box
                component="span"
                sx={{
                  minWidth: 18,
                  height: 18,
                  px: 0.5,
                  borderRadius: 9,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  color,
                  backgroundColor: active ? activeBg : inactiveBg,
                }}
              >
                {counts[kind]}
              </Box>
            </ButtonBase>
          );
        })}
      </Stack>
    </Box>
  );
};
